import { format, parseISO } from 'date-fns';
import { DEFAULT_TIMES } from '../constants/editJobConstants';

/**
 * Format time for display in AM/PM format
 */
export const formatTimeForDisplay = (timeString) => {
  if (!timeString) return '';
  
  try {
    const [hoursStr, minutesStr] = timeString.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    
    return `${hours}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    console.error('Error formatting time for display:', error);
    return timeString;
  }
};

/**
 * Create ISO string from date and time components
 */
export const createISOString = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  
  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hours, minutes] = timeStr.split(':').map(Number);
    
    return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00.000Z`;
  } catch (error) {
    console.error('Error creating ISO string:', error);
    return null;
  }
};

/**
 * Parse override date time to separate date and time values
 */
export const parseOverrideDateTime = (overrideDateTime, fallbackTime = DEFAULT_TIMES.CHECK_IN) => {
  if (!overrideDateTime) {
    return { date: '', time: fallbackTime };
  }

  try {
    const isoString = overrideDateTime;
    const matches = isoString.match(/T(\d{2}):(\d{2}):(\d{2})/);
    
    if (matches && matches.length >= 3) {
      const hours = matches[1];
      const minutes = matches[2];
      const time = `${hours}:${minutes}`;
      const date = format(parseISO(isoString), 'yyyy-MM-dd');
      return { date, time };
    } else {
      // Fallback to Date parsing
      const dateTime = new Date(overrideDateTime);
      const time = format(dateTime, 'HH:mm');
      const date = format(dateTime, 'yyyy-MM-dd');
      return { date, time };
    }
  } catch (error) {
    console.error('Error parsing override date time:', error);
    return { date: '', time: fallbackTime };
  }
};

/**
 * Get current reservation data based on view
 */
export const getCurrentReservationData = (reservationView, job, previousReservation, nextReservation) => {
  switch (reservationView) {
    case 'previous': return previousReservation;
    case 'next': return nextReservation;
    default: return job?.reservation;
  }
};

/**
 * Check if user has required role for job updates
 */
export const hasRequiredRole = (userRole, requiredRoles) => {
  return userRole && requiredRoles.includes(userRole);
};

/**
 * Get submit button text based on tab and loading state
 */
export const getSubmitButtonText = (activeTab, updateLoading) => {
  if (updateLoading) {
    return activeTab === 'job' ? 'Saving...' : 'Updating...';
  }
  
  return activeTab === 'job' ? 'Save Job Changes' : 'Close';
};

/**
 * Build reservation update input object
 */
export const buildReservationUpdateInput = (reservationId, field, dateValue, timeValue, currentReservation) => {
  const isCheckIn = field === 'checkin';
  const isoString = createISOString(dateValue, timeValue);
  
  const updateInput = {
    id: reservationId,
    has_time_override: true
  };
  
  if (isCheckIn) {
    updateInput.override_check_in = isoString;
    // Keep existing checkout override if it exists
    if (currentReservation.override_check_out) {
      updateInput.override_check_out = currentReservation.override_check_out;
    }
  } else {
    updateInput.override_check_out = isoString;
    // Keep existing checkin override if it exists
    if (currentReservation.override_check_in) {
      updateInput.override_check_in = currentReservation.override_check_in;
    }
  }
  
  return updateInput;
};